import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { overlayPresenceProps } from '../hooks/mistRail.js';
import { focusFirstFocusable, getFocusableElements } from '../utils/focusTrap.js';
import { cx } from '../utils/cx.js';
import styles from './Popover.module.css';

export type PopoverProps = {
  opened: boolean;
  onChange: (open: boolean) => void;
  target: ReactNode;
  children: ReactNode;
  /**
   * Dialog semantics + focus trap (use with `portal` for large panels).
   * Requires `panelId` and `ariaLabelledBy` for accessible naming.
   */
  modal?: boolean;
  /** `id` on the panel element (required when `modal`). */
  panelId?: string;
  /** id of the visible title element (`aria-labelledby`). */
  ariaLabelledBy?: string;
  /**
   * Render panel in `document.body` with fixed position (avoids overflow clipping in scroll containers).
   * Recomputes position on scroll/resize while open.
   */
  portal?: boolean;
  /** Horizontal alignment of the panel relative to the anchor (portal mode). */
  align?: 'start' | 'end';
  /** Min width for portal panel (default 200). */
  portalMinWidth?: number;
  /** Max width for portal panel (default 320). Raise for wide panels (e.g. wizards). */
  portalMaxWidth?: number;
  /** Estimated panel height for auto vertical placement in portal mode. */
  portalEstimatedHeight?: number;
  /** Optional z-index override for portal panel. */
  portalZIndex?: number | string;
  /** When false, target click does not toggle (controlled `opened` only). Default true. */
  toggleOnTargetClick?: boolean;
};

export function Popover({
  opened,
  onChange,
  target,
  children,
  portal = false,
  align = 'start',
  portalMinWidth = 200,
  portalMaxWidth = 320,
  portalEstimatedHeight = 280,
  portalZIndex,
  toggleOnTargetClick = true,
  modal = false,
  panelId,
  ariaLabelledBy,
}: PopoverProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const [portalPos, setPortalPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const resolvedPortalZIndex =
    portalZIndex ?? (modal && portal ? 'var(--wp-z-popover-over-modal)' : undefined);

  const updatePortalPos = useCallback(
    (measuredPanelHeight?: number) => {
      const el = anchorRef.current;
      if (!el || !portal) return;
      const r = el.getBoundingClientRect();
      const width = Math.min(portalMaxWidth, Math.max(portalMinWidth, window.innerWidth - 16));
      const gap = 6;
      const panelH = Math.max(
        1,
        measuredPanelHeight ?? panelRef.current?.getBoundingClientRect().height ?? portalEstimatedHeight,
      );
      const anchorMissing = r.width < 2 && r.height < 2;
      let left: number;
      let top: number;
      if (anchorMissing) {
        left = Math.max(8, (window.innerWidth - width) / 2);
        const est = Math.min(panelH, window.innerHeight - 24);
        top = Math.max(8, (window.innerHeight - est) / 2);
      } else {
        left = align === 'end' ? r.right - width : r.left;
        left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
        const spaceBelow = window.innerHeight - r.bottom - 8;
        const spaceAbove = r.top - 8;
        const fitsBelow = spaceBelow >= panelH + gap;
        const fitsAbove = spaceAbove >= panelH + gap;
        const placeBelow = fitsBelow || (!fitsAbove && spaceBelow >= spaceAbove);
        top = placeBelow ? r.bottom + gap : r.top - panelH - gap;
        top = Math.max(8, Math.min(top, window.innerHeight - panelH - 8));
      }
      setPortalPos((prev) => {
        const next = { top, left, width };
        if (prev && prev.top === next.top && prev.left === next.left && prev.width === next.width) {
          return prev;
        }
        return next;
      });
    },
    [portal, align, portalMinWidth, portalMaxWidth, portalEstimatedHeight],
  );

  useLayoutEffect(() => {
    if (!opened || !portal) {
      setPortalPos(null);
      return;
    }
    updatePortalPos();
    const raf = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const h = Math.ceil(panel.getBoundingClientRect().height);
      if (h > 0) updatePortalPos(h);
    });
    return () => cancelAnimationFrame(raf);
  }, [opened, portal, updatePortalPos, children]);

  useEffect(() => {
    if (!opened || !portal) return;
    const onMove = () => updatePortalPos();
    window.addEventListener('resize', onMove);
    document.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      document.removeEventListener('scroll', onMove, true);
    };
  }, [opened, portal, updatePortalPos]);

  useEffect(() => {
    if (!opened) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onChange(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [opened, onChange]);

  useEffect(() => {
    if (!opened || !modal) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (panel) focusFirstFocusable(panel);
    });
    return () => {
      cancelAnimationFrame(raf);
      returnFocusRef.current?.focus?.();
      returnFocusRef.current = null;
    };
  }, [opened, modal]);

  useEffect(() => {
    if (!opened || !modal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel?.contains(document.activeElement)) return;
      const focusables = getFocusableElements(panel);
      if (focusables.length < 2) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [opened, modal]);

  useEffect(() => {
    if (!opened) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      onChange(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [opened, onChange]);

  const toggle = () => onChange(!opened);

  const wrapTarget = (): ReactNode => {
    if (isValidElement(target)) {
      const el = target as ReactElement<{
        onClick?: (e: ReactMouseEvent) => void;
        'aria-expanded'?: boolean;
        'aria-haspopup'?: string | boolean;
        'aria-controls'?: string;
      }>;
      return cloneElement(el, {
        onClick: (e: ReactMouseEvent) => {
          el.props.onClick?.(e);
          e.stopPropagation();
          if (toggleOnTargetClick) toggle();
        },
        'aria-expanded': opened,
        ...(modal
          ? {
              'aria-haspopup': 'dialog' as const,
              ...(panelId && opened ? { 'aria-controls': panelId } : {}),
            }
          : {}),
      });
    }
    return (
      <span
        onClick={(e) => {
          e.stopPropagation();
          if (toggleOnTargetClick) toggle();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        role="button"
        tabIndex={0}
      >
        {target}
      </span>
    );
  };

  const panelClass = cx(
    portal ? styles.panelPortal : styles.panel,
    modal && portal ? styles.panelPortalModal : null,
  );

  const panelInner = (
    <div
      ref={modal || portal ? panelRef : undefined}
      id={panelId}
      role={modal ? 'dialog' : undefined}
      aria-modal={modal ? true : undefined}
      aria-labelledby={modal && ariaLabelledBy ? ariaLabelledBy : undefined}
      className={panelClass}
      tabIndex={modal ? -1 : undefined}
      {...overlayPresenceProps(opened)}
      style={
        portal && portalPos
          ? {
              top: portalPos.top,
              left: portalPos.left,
              width: portalPos.width,
              zIndex: resolvedPortalZIndex,
            }
          : undefined
      }
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );

  return (
    <div
      className={styles.anchor}
      ref={anchorRef}
      data-datatable-rowclick-ignore
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {wrapTarget()}
      {opened && !portal ? panelInner : null}
      {opened && portal && portalPos
        ? createPortal(panelInner, document.body)
        : null}
    </div>
  );
}
